import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Flex, TextV2, JoyKitThemeProvider } from '@withjoy/joykit';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { display6ToEventTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { useTranslation, useHistory } from '@shared/core';
import type { useCashFundGuestController } from '../CashFundGuest.controller';
import { ButtonV2 } from '@withjoy/joykit';
import { Container, FundCard, FundPhotoContainer, FundPhoto, ProgressBarContainer, ProgressBarFill, ProgressBarPreview, styles } from './CashFundGuestBody.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { LoadingExpressCheckoutElement, isSomePaymentMethodAvailable } from '@shared/core/stripe';
import { useCreateCashFundPaymentIntentMutation, useCalculateCashFundContributionReceiptLazyQuery } from '../../../CashFunds.graphql.generated';
import type { StripeExpressCheckoutElementClickEvent, StripeExpressCheckoutElementConfirmEvent, LineItem, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { majorToMinorUnits, minorToMajorUnits } from '@shared/utils/currency';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { InlineContributorForm } from './InlineContributorForm';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { sanitizeContributorNote, isValidContributorNote } from '../../../utils/sanitizeNote';
import { ContributionFormFields } from './ContributionFormFields';
import { useCashFundsTelemetry } from '../../../CashFunds.telemetry';

const ANY_AMOUNT_MIN_MAJOR_UNITS = 1;
const ANY_AMOUNT_MAX_MAJOR_UNITS = 10_000;

export interface CashFundGuestBodyProps
  extends Readonly<{
    fundId: string;
    event: GuestSiteEvent;
    controllerData: ReturnType<typeof useCashFundGuestController>;
  }> {}

const FundPhotoSection: React.FC<{ photoUrl: string; alt: string }> = ({ photoUrl, alt }) => (
  <FundPhotoContainer
    width="100%"
    position="relative"
    borderRadius={3}
    overflow="hidden"
  >
    <FundPhoto
      src={photoUrl}
      alt={alt}
    />
  </FundPhotoContainer>
);

const ProgressSection: React.FC<{
  formattedTotalRaised: string;
  formattedGoal: string;
  progressPercentage: number;
  projectedPercentage?: number;
}> = ({ formattedTotalRaised, formattedGoal, progressPercentage, projectedPercentage }) => (
  <Flex
    flexDirection="column"
    gap={2}
    alignItems="stretch"
  >
    <Flex
      justifyContent="space-between"
      alignItems="center"
    >
      <TextV2
        typographyVariant="label3"
        color="mono14"
      >
        {formattedTotalRaised}
      </TextV2>
      <TextV2
        typographyVariant="label2"
        color="mono10"
        textAlign="right"
      >
        {formattedGoal}
      </TextV2>
    </Flex>
    <ProgressBarContainer
      width="100%"
      backgroundColor="rgba(0, 0, 0, 0.1)"
      borderRadius="100px"
      overflow="hidden"
      role="progressbar"
      aria-valuenow={Math.round(progressPercentage)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Fund progress"
    >
      {projectedPercentage !== undefined && projectedPercentage > progressPercentage && <ProgressBarPreview width={`${Math.min(projectedPercentage, 100)}%`} />}
      <ProgressBarFill width={`${progressPercentage}%`} />
    </ProgressBarContainer>
  </Flex>
);

type ContributionButtonsProps = Readonly<{
  fundId: string;
  fundName: string;
  amountInMinorUnits: number;
  currencyCode: string;
  isAnyAmount: boolean;
  customAmount: string;
  onCustomAmountChange: (amount: string) => void;
  hostNames: string;
  fundImage?: string;
  inviteFullName?: string | null;
  inviteEmail?: string | null;
}>;

const ContributionButtons: React.FC<ContributionButtonsProps> = ({
  fundId,
  fundName,
  amountInMinorUnits,
  currencyCode,
  isAnyAmount,
  customAmount,
  onCustomAmountChange,
  hostNames,
  inviteFullName,
  inviteEmail,
}) => {
  const { tq } = useTranslation('cashFundsGuest');
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const queryParams = useQueryParams();
  const telemetry = useCashFundsTelemetry();
  const guestKey = typeof queryParams.guestKey === 'string' ? queryParams.guestKey : null;
  const [error, setError] = useState<string | null>(null);
  const [createCashFundPaymentIntent] = useCreateCashFundPaymentIntentMutation();
  const [calculateReceipt, { data: receiptData, error: feeCalculationError, called: feeQueryCalled }] = useCalculateCashFundContributionReceiptLazyQuery();
  const [totalWithFees, setTotalWithFees] = useState<number>(amountInMinorUnits);
  const [fees, setFees] = useState<Array<{ amount: { valueInMinorUnits: number } }>>([]);
  const currencySymbol = getCurrencySymbolFromCurrencyCode(currencyCode);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);

  // Formik for amount and note validation
  const validationSchema = useMemo(() => {
    const noteValidation = Yup.string().test('no-html-chars', tq`Special characters < and > are not allowed`, isValidContributorNote);

    if (!isAnyAmount) {
      return Yup.object({
        note: noteValidation,
      });
    }
    return Yup.object({
      amount: Yup.number()
        .required(tq`Amount is required`)
        .positive(tq`Please enter a valid amount`)
        .min(ANY_AMOUNT_MIN_MAJOR_UNITS, tq`Minimum contribution is ${currencySymbol}${ANY_AMOUNT_MIN_MAJOR_UNITS}`)
        .max(ANY_AMOUNT_MAX_MAJOR_UNITS, tq`Maximum contribution is ${currencySymbol}${ANY_AMOUNT_MAX_MAJOR_UNITS.toLocaleString()}`)
        .typeError(tq`Please enter a valid amount`),
      note: noteValidation,
    });
  }, [isAnyAmount, tq, currencySymbol]);

  const formik = useFormik({
    initialValues: {
      amount: customAmount,
      note: '',
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Form submission is handled by Express Checkout or Contribute Another Way
    },
  });

  // Calculate the actual amount to use (custom amount in minor units or fixed amount)
  const actualAmountInMinorUnits = useMemo(() => {
    if (isAnyAmount) {
      const customAmountNumber = parseFloat(formik.values.amount) || 0;
      return majorToMinorUnits(customAmountNumber, currencyCode);
    }
    return amountInMinorUnits;
  }, [isAnyAmount, formik.values.amount, amountInMinorUnits, currencyCode]);

  const [debouncedAmount, setDebouncedAmount] = useState(actualAmountInMinorUnits);

  // Debounce amount changes to prevent excessive queries
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAmount(actualAmountInMinorUnits);
    }, 500);

    return () => clearTimeout(timer);
  }, [actualAmountInMinorUnits]);

  // Fee calculation is informational only - Stripe charges actual fees at checkout
  // We show estimated fees to users but never block checkout if calculation fails
  useEffect(() => {
    if (!debouncedAmount) {
      return;
    }

    calculateReceipt({
      variables: {
        input: {
          cashFundId: fundId,
          contributionAmountInMinorUnits: debouncedAmount,
        },
      },
    });
  }, [debouncedAmount, fundId, calculateReceipt]);

  // Update fees when receipt data is received, or fallback to base amount on error
  useEffect(() => {
    const receipt = receiptData?.calculateCashFundContributionReceipt;

    if (receipt?.total && receipt.fees) {
      setTotalWithFees(receipt.total.valueInMinorUnits);
      setFees(receipt.fees);
      setError(null);
    } else if (feeQueryCalled && (feeCalculationError || !receipt)) {
      if (feeCalculationError) {
        telemetry.queryError('calculateCashFundContributionReceipt', feeCalculationError, fundId);
        setError(tq`Unable to calculate fees. Fees will be shown at checkout.`);
      }
      // Fallback to base amount if fee calculation fails (fees are informational only)
      setTotalWithFees(debouncedAmount);
      setFees([]);
    }
  }, [receiptData, debouncedAmount, feeCalculationError, feeQueryCalled, setError, tq, telemetry, fundId]);

  // Memoize Stripe Elements update options to prevent unnecessary updates
  const stripeElementsUpdateOptions = useMemo(
    () => ({
      amount: totalWithFees,
      currency: currencyCode.toLowerCase(),
      mode: 'payment' as const,
    }),
    [totalWithFees, currencyCode],
  );

  // Update Elements with the total including fees
  useEffect(() => {
    if (!elements || !totalWithFees) {
      return;
    }
    elements.update(stripeElementsUpdateOptions);
  }, [elements, stripeElementsUpdateOptions, totalWithFees]);

  const handleCheckoutClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      // Clear any previous errors when user attempts new checkout
      if (error) setError(null);

      // Validate amount for any amount flow
      if (isAnyAmount) {
        formik.setFieldTouched('amount', true);
        if (formik.errors.amount) {
          setError(formik.errors.amount);
          event.resolve({ lineItems: [] });
          return;
        }
      }

      telemetry.expressCheckoutClicked(fundId, actualAmountInMinorUnits, currencyCode);

      const stripeLineItems: LineItem[] = [
        {
          name: fundName,
          amount: actualAmountInMinorUnits,
        },
      ];

      fees.forEach(fee => {
        stripeLineItems.push({
          name: tq`Processing Fee`,
          amount: fee.amount.valueInMinorUnits,
        });
      });

      event.resolve({
        billingAddressRequired: true,
        emailRequired: true,
        lineItems: stripeLineItems,
      });
    },
    [error, fundName, actualAmountInMinorUnits, fees, isAnyAmount, formik, tq, telemetry, fundId, currencyCode],
  );

  const handleCheckoutConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements || !event.billingDetails?.email) {
        setError(tq`Payment information is incomplete`);
        event.paymentFailed({ reason: 'fail' });
        return;
      }

      try {
        setError(null);

        // Validate amount for "any amount" contributions
        if (isAnyAmount) {
          const minAmountInMinorUnits = majorToMinorUnits(ANY_AMOUNT_MIN_MAJOR_UNITS, currencyCode);
          const maxAmountInMinorUnits = majorToMinorUnits(ANY_AMOUNT_MAX_MAJOR_UNITS, currencyCode);

          if (actualAmountInMinorUnits < minAmountInMinorUnits) {
            setError(tq`Minimum contribution is ${currencySymbol}${ANY_AMOUNT_MIN_MAJOR_UNITS}`);
            event.paymentFailed({ reason: 'fail' });
            return;
          }
          if (actualAmountInMinorUnits > maxAmountInMinorUnits) {
            setError(tq`Maximum contribution is ${currencySymbol}${ANY_AMOUNT_MAX_MAJOR_UNITS.toLocaleString()}`);
            event.paymentFailed({ reason: 'fail' });
            return;
          }
        }

        const contributorFullName = inviteFullName ?? event.shippingAddress?.name ?? event.billingDetails.name ?? '';
        const contributorEmail = inviteEmail ?? event.billingDetails.email;
        const { data } = await createCashFundPaymentIntent({
          variables: {
            input: {
              amountInMinorUnits: actualAmountInMinorUnits,
              cashFundId: fundId,
              contributorEmail,
              contributorFullName,
              contributorMessage: sanitizeContributorNote(formik.values.note),
              guestKey: guestKey || null,
            },
          },
        });

        const paymentIntent = data?.createCashFundPaymentIntent;

        if (paymentIntent?.__typename === 'CashFundGeneralError') {
          telemetry.mutationError('createCashFundPaymentIntent', new Error(paymentIntent.code || 'Payment failed'), fundId);
          setError(tq`Payment failed. Please try again.`);
          event.paymentFailed({ reason: 'fail' });
          return;
        }

        if (paymentIntent?.__typename !== 'CashFundPaymentIntent' || !paymentIntent.clientSecret) {
          telemetry.mutationError('createCashFundPaymentIntent', new Error('Invalid payment intent response'), fundId);
          setError(tq`Payment failed. Please try again.`);
          event.paymentFailed({ reason: 'fail' });
          return;
        }

        const returnUrl = window.location.origin + window.location.pathname + '/success?paymentId=' + paymentIntent.paymentAttempt.id;

        const { error: confirmError } = await stripe.confirmPayment({
          elements,
          clientSecret: paymentIntent.clientSecret,
          confirmParams: {
            return_url: returnUrl,
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          telemetry.mutationError('stripeConfirmPayment', new Error(confirmError.message || 'Payment confirmation failed'), fundId);
          setError(confirmError.message || tq`Payment failed. Please try again.`);
          event.paymentFailed({ reason: 'fail' });
          return;
        }

        // Payment successful - manually redirect since redirect: 'if_required' only redirects for auth challenges
        const returnUrlObj = new URL(returnUrl);
        history.replace(returnUrlObj.pathname + returnUrlObj.search);
      } catch (err) {
        telemetry.mutationError('createCashFundPaymentIntent', err instanceof Error ? err : new Error('Unexpected error'), fundId);
        setError(tq`An unexpected error occurred. Please try again.`);
        event.paymentFailed({ reason: 'fail' });
      }
    },
    [
      stripe,
      elements,
      fundId,
      actualAmountInMinorUnits,
      createCashFundPaymentIntent,
      formik.values.note,
      guestKey,
      inviteFullName,
      inviteEmail,
      tq,
      history,
      isAnyAmount,
      currencySymbol,
      currencyCode,
      telemetry,
    ],
  );

  const handleContributeAnotherWay = useCallback(() => {
    // Clear any previous errors
    if (error) setError(null);

    // Validate amount for any amount flow
    if (isAnyAmount) {
      formik.setFieldTouched('amount', true);
      if (formik.errors.amount) {
        return;
      }
    }
    telemetry.contributeAnotherWayClicked(fundId, actualAmountInMinorUnits, currencyCode);
    setShowCheckoutForm(true);
  }, [error, isAnyAmount, formik, telemetry, fundId, actualAmountInMinorUnits, currencyCode]);

  const handleExpressCheckoutReady = useCallback((event: StripeExpressCheckoutElementReadyEvent) => {
    const hasPaymentMethods = event.availablePaymentMethods && isSomePaymentMethodAvailable(event.availablePaymentMethods);
    if (!hasPaymentMethods) {
      setShowCheckoutForm(true);
    }
  }, []);

  return (
    <JoyKitThemeProvider>
      <Flex
        flexDirection="column"
        gap={4}
        paddingX={0}
        paddingBottom={0}
        paddingTop={0}
        alignItems="stretch"
      >
        {error && (
          <TextV2
            typographyVariant="body1"
            color="red9"
            textAlign="center"
          >
            {error}
          </TextV2>
        )}

        <ContributionFormFields
          isAnyAmount={isAnyAmount}
          formik={formik}
          currencySymbol={currencySymbol}
          currencyCode={currencyCode}
          fundId={fundId}
          onCustomAmountChange={value => {
            // Clear any previous errors when user changes amount
            if (error) setError(null);
            onCustomAmountChange(value);
          }}
          labelColor={showCheckoutForm ? 'mono14' : 'mono11'}
        />

        {!showCheckoutForm && (
          <>
            <LoadingExpressCheckoutElement
              onClick={handleCheckoutClick}
              onConfirm={handleCheckoutConfirm}
              onReady={handleExpressCheckoutReady}
              options={{
                buttonHeight: 50,
                paymentMethods: {
                  applePay: 'auto',
                  googlePay: 'auto',
                  amazonPay: 'never',
                  link: 'never',
                  paypal: 'never',
                  klarna: 'never',
                },
                layout: {
                  overflow: 'never',
                },
              }}
              lowerContent={
                <ButtonV2
                  variant="outline"
                  intent="neutral"
                  shape="rounded"
                  size="lg"
                  width="100%"
                  marginTop={4}
                  typographyVariant="button2"
                  onClick={handleContributeAnotherWay}
                >
                  {tq`Contribute Another Way`}
                </ButtonV2>
              }
            />
          </>
        )}

        {showCheckoutForm && (
          <InlineContributorForm
            fundId={fundId}
            amountInMinorUnits={actualAmountInMinorUnits}
            hostNames={hostNames}
            currencyCode={currencyCode}
            note={formik.values.note}
            inviteFullName={inviteFullName}
            inviteEmail={inviteEmail}
          />
        )}
      </Flex>
    </JoyKitThemeProvider>
  );
};

export const CashFundGuestBody: React.FC<CashFundGuestBodyProps> = ({ fundId: _fundId, event, controllerData }) => {
  const { tq } = useTranslation('cashFundsGuest');

  // Use controller data passed from parent - loading/error handled by CashFundGuestContent
  const { fund, progressPercentage, formattedTotalRaised, formattedGoal, inviteFullName, inviteEmail, hostName, formattedPerPerson, goalAmount, totalRaised } = controllerData;

  const requestingName = useMemo(() => {
    if (hostName) return hostName;
    const displayName = event.info?.eventDisplayName || '';
    return displayName.split(' & ')[0] || displayName.split(' and ')[0] || displayName;
  }, [hostName, event.info?.eventDisplayName]);

  const contributionAmountInMinorUnits = useMemo(() => {
    return fund?.cashFund?.fixedContributionAmount?.valueInMinorUnits || 0;
  }, [fund?.cashFund?.fixedContributionAmount]);

  const isAnyAmount = contributionAmountInMinorUnits === 0;

  // Calculate default amount for any amount flow
  const defaultAmount = useMemo(() => {
    if (!isAnyAmount) return '';

    const defaultValue = 100;

    // If there's a goal, calculate remaining amount
    if (goalAmount && totalRaised !== undefined) {
      const remaining = Math.max(0, goalAmount - totalRaised);

      // If goal itself is less than $100, use the goal
      if (goalAmount < defaultValue) {
        return goalAmount.toString();
      }

      // If remaining is less than $100, use remaining
      if (remaining > 0 && remaining < defaultValue) {
        return remaining.toString();
      }
    }

    // Default to $100
    return defaultValue.toString();
  }, [isAnyAmount, goalAmount, totalRaised]);

  const [customAmount, setCustomAmount] = useState<string>(defaultAmount);

  const currencyCode = fund?.cashFund?.currencyCode || DEFAULT_CURRENCY;

  // Calculate projected progress with contribution
  const projectedPercentage = useMemo(() => {
    if (!goalAmount || !totalRaised) return undefined;

    const contributionInMajorUnits = isAnyAmount ? parseFloat(customAmount) || 0 : minorToMajorUnits(contributionAmountInMinorUnits, currencyCode);

    if (contributionInMajorUnits <= 0) return undefined;

    const projectedTotal = totalRaised + contributionInMajorUnits;
    return Math.min((projectedTotal / goalAmount) * 100, 100);
  }, [goalAmount, totalRaised, isAnyAmount, customAmount, contributionAmountInMinorUnits, currencyCode]);

  // Loading/error states are handled by CashFundGuestContent - fund is guaranteed to exist here
  if (!fund) {
    return null;
  }

  return (
    <Container
      flexDirection="column"
      alignItems="center"
      gap={[4, null, pxToRem(56)]}
      width="100%"
      marginX="auto"
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={[2, null, pxToRem(10)]}
        width="100%"
        paddingX={[3, null, 3]}
        paddingTop={[6, null, 0]}
        paddingBottom={0}
      >
        <GuestSiteTypographyOverride override={display6ToEventTitleOverride}>
          <TextV2
            typographyVariant="display6"
            textAlign="center"
            wordBreak="break-word"
          >
            {event.info?.eventDisplayName || ''}
          </TextV2>
        </GuestSiteTypographyOverride>
      </Flex>

      <Flex
        flexDirection="column"
        alignItems="center"
        gap={[4, null, pxToRem(96)]}
        width="100%"
        paddingX={[6, null, styles.desktopPaddingX]}
      >
        <FundCard
          borderRadius={3}
          width="100%"
          overflow="hidden"
        >
          <Flex
            flexDirection="column"
            gap={0}
            alignItems="stretch"
          >
            {fund.cashFund.photo?.url && (
              <FundPhotoSection
                photoUrl={fund.cashFund.photo.url}
                alt={fund.cashFund.name}
              />
            )}

            <Flex
              flexDirection="column"
              gap={2}
              paddingTop={4}
              paddingBottom={6}
              paddingX={0}
              alignItems="stretch"
            >
              <Flex
                flexDirection="column"
                gap={4}
                alignItems="stretch"
              >
                <Flex
                  flexDirection="column"
                  gap={2}
                  alignItems="stretch"
                >
                  <Flex
                    flexDirection="column"
                    gap={4}
                    alignItems="stretch"
                  >
                    <Flex
                      flexDirection="column"
                      gap={1}
                      alignItems="stretch"
                    >
                      <TextV2
                        typographyVariant="hed6"
                        color="mono14"
                        wordBreak="break-word"
                      >
                        {fund.cashFund.name}
                      </TextV2>
                      {formattedPerPerson && (
                        <TextV2
                          typographyVariant="hed1"
                          color="mono14"
                        >
                          {tq`${formattedPerPerson} per person`}
                        </TextV2>
                      )}
                    </Flex>

                    {fund.cashFund.note && (
                      <TextV2
                        typographyVariant="body1"
                        color="mono12"
                        wordBreak="break-word"
                        whiteSpace="pre-wrap"
                      >
                        {fund.cashFund.note}
                      </TextV2>
                    )}
                  </Flex>
                </Flex>

                {goalAmount && formattedGoal && (
                  <ProgressSection
                    formattedTotalRaised={formattedTotalRaised}
                    formattedGoal={formattedGoal}
                    progressPercentage={progressPercentage}
                    projectedPercentage={projectedPercentage}
                  />
                )}
              </Flex>
            </Flex>

            <ContributionButtons
              fundId={fund.cashFund?.id || _fundId}
              fundName={fund.cashFund?.name || ''}
              amountInMinorUnits={contributionAmountInMinorUnits}
              currencyCode={currencyCode}
              isAnyAmount={isAnyAmount}
              customAmount={customAmount}
              onCustomAmountChange={setCustomAmount}
              hostNames={requestingName}
              fundImage={fund.cashFund?.photo?.url}
              inviteFullName={inviteFullName}
              inviteEmail={inviteEmail}
            />
          </Flex>
        </FundCard>
      </Flex>
    </Container>
  );
};
